import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './contrast-checker.component.module.scss';
import { genStyles } from '../converter-layout/generator-layout';

interface State {
    fg: string;
    bg: string;
}

function hexToRgb(hex: string): [number, number, number] | null {
    const m = hex.replace('#', '').match(/^([0-9a-f]{6}|[0-9a-f]{3})$/i);
    if (!m) return null;
    let h = m[0];
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    const v = parseInt(h, 16);
    return [(v >> 16) & 0xff, (v >> 8) & 0xff, v & 0xff];
}

function relativeLuminance(rgb: [number, number, number]): number {
    const [r, g, b] = rgb.map(c => {
        const v = c / 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(fg: string, bg: string): number | null {
    const f = hexToRgb(fg);
    const b = hexToRgb(bg);
    if (!f || !b) return null;
    const l1 = relativeLuminance(f);
    const l2 = relativeLuminance(b);
    const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1];
    return (hi + 0.05) / (lo + 0.05);
}

export default class ContrastCheckerComponent extends Component<{ feature?: string }, State> {
    state: State = { fg: '#1e293b', bg: '#ffffff' };

    private get ratio(): number | null {
        return contrastRatio(this.state.fg, this.state.bg);
    }

    render() {
        const { fg, bg } = this.state;
        const ratio = this.ratio;
        const passed = (target: number) => ratio !== null && ratio >= target;
        return (
            <div className="row">
                <div className="col-12 col-md-6">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>
                            Foreground (text)
                            <div className={styles.colorRow}>
                                <input type="color" value={fg} onChange={e => this.setState({ fg: e.target.value })} />
                                <input className={styles.input} type="text" value={fg}
                                    onChange={e => this.setState({ fg: e.target.value })} />
                            </div>
                        </label>
                        <label className={sharedStyles.label}>
                            Background
                            <div className={styles.colorRow}>
                                <input type="color" value={bg} onChange={e => this.setState({ bg: e.target.value })} />
                                <input className={styles.input} type="text" value={bg}
                                    onChange={e => this.setState({ bg: e.target.value })} />
                            </div>
                        </label>
                    </div>
                    <div className={genStyles.section}>
                        <div className={styles.ratioBox}>
                            <span className={styles.ratioLabel}>Contrast ratio</span>
                            <span className={styles.ratioValue}>{ratio !== null ? ratio.toFixed(2) + ':1' : 'invalid color'}</span>
                        </div>
                        <table className={styles.wcag}>
                            <thead><tr><th></th><th>Normal text</th><th>Large text (18pt+)</th></tr></thead>
                            <tbody>
                                <tr>
                                    <td>WCAG AA</td>
                                    <td className={passed(4.5) ? styles.pass : styles.fail}>{passed(4.5) ? '✓ Pass' : '✗ Fail'} (≥ 4.5)</td>
                                    <td className={passed(3.0) ? styles.pass : styles.fail}>{passed(3.0) ? '✓ Pass' : '✗ Fail'} (≥ 3.0)</td>
                                </tr>
                                <tr>
                                    <td>WCAG AAA</td>
                                    <td className={passed(7.0) ? styles.pass : styles.fail}>{passed(7.0) ? '✓ Pass' : '✗ Fail'} (≥ 7.0)</td>
                                    <td className={passed(4.5) ? styles.pass : styles.fail}>{passed(4.5) ? '✓ Pass' : '✗ Fail'} (≥ 4.5)</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="col-12 col-md-6">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>Preview</label>
                        <div className={styles.preview} style={{ background: bg, color: fg }}>
                            <h2 style={{ margin: 0, fontSize: '2rem' }}>Large text 24pt</h2>
                            <p style={{ margin: '0.75rem 0 0 0', fontSize: '1.1rem' }}>
                                Normal body copy at 18pt. The quick brown fox jumps over the lazy dog.
                            </p>
                            <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.95rem' }}>
                                Smaller body copy at 14pt. The quick brown fox jumps over the lazy dog.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
